import React, {useState, useRef} from 'react';
import {Glyphs, ControlButton, TscButtonType, TscThemeNames, TscStyles, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import {SearchBarInput, SearchBarContainer, SearchForm} from '../util/StyledElements';
import styled from 'styled-components';
import {ButtonActivationKeys} from '../../constants/Constants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import NotificationToggle from './NotificationToggle';
import config from '../../service/config';
import {commentsStore} from '../../context/commentsProvider';
import {IComment} from '../../model/commentModel';
import {selectedConversationStore} from '../../context/selectedConversationProvider';

const ClearButtonWrapper = styled.div<{isVisible: boolean; inputHasFocus: boolean}>`
   display: ${props => (props.isVisible ? 'block' : 'none')};
   visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
   transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
   transform: ${props => (props.isVisible ? 'translateX(0)' : 'translateX(100%)')};
   opacity: ${props => (props.isVisible ? 1 : 0)};
   margin-left: auto;
`;

const NotificationToggleWrapper = styled.div`
   display: block;
   visibility: visible;
   transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
   transform: translateX(0);

   svg {
      height: 0.875rem;
      width: 0.875rem;
   }
`;

const CommentCountLabel = styled.label<{hasSearchText: boolean; hasFocus: boolean}>`
   font-weight: 600;
   white-space: nowrap;
   font-size: 0.875rem;
   transition: all .3s ease-in-out;
   opacity: ${props => (props.hasFocus ? 0 : 1)};
   transform: ${props => (props.hasFocus ? 'translateX(-100%)' : 'translateX(0)')};
   overflow: hidden;
   width: ${props => (props.hasFocus ? '0' : '8.25rem')};
`;

export const CommentFilterWrapper = styled.div<{hasSearchText: boolean}>`
   width: 100%;
`;

const CommentSearchWrapper = styled.div<{theme: TscThemeNames; hasFocus: boolean}>`
   background-color: ${props => props.hasFocus ? props.theme === TscThemeName.dusk ? TscStyles.color.action.secondaryDusk : TscStyles.color.ui.dawn.medium : 'transparent'};
   display: flex;
   align-items: center;
   width: ${props => props.hasFocus ? '100%' : '32px'};
   margin-left: auto;
   position: relative;  
   transition: all 0.3s ease-in-out;
   border-radius: 8px;
`;

const CommentSearchInput = styled(SearchBarInput)<{hasFocus: boolean}>`
   flex-grow: ${props => (props.hasFocus ? 1 : 0)};
   width: ${props => (props.hasFocus ? '100%' : '0')};
   padding: ${props => (props.hasFocus ? '0 0 0 8px' : 0)};
   max-width: 100%;
   transition: all 0.3s ease-in-out;
`;

const CommentSearchForm = styled(SearchForm)<{hasFocus: boolean}>`
   display: flex;
   align-items: center;
   width: 100%;
   justify-content: flex-end;
   ${props => props.hasFocus ? 'margin-left: 0' : ''};
`;
const CommentSearchBarContainer = styled(SearchBarContainer)`
   display: flex;
   align-items: center;
   position: relative;
   width: 100%;
`;

const SearchButtonWrapper = styled.div`
   margin-left: auto;
`;

export const CommentFilterBase: React.FC<ICommentFilterProps & IStateMappedProps> = ({searchText, setSearchText, executeSearch, comments, VideoReviewId}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const inputRef = useRef<HTMLInputElement>(null);
   const [hasFocus, setHasFocus] = useState(false);

   const onSearchButtonClicked = (event: React.MouseEvent) => {
      event.preventDefault();
      setHasFocus(true);
      if (searchText.length) {
         executeSearch();
      }

      if (inputRef.current) {
         inputRef.current.focus();
      }
   };

   const onKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === ButtonActivationKeys.enter && !e.shiftKey) {
         e.stopPropagation();
         e.preventDefault();

         if (searchText.trim().length) {
            executeSearch();
         }
      }
   };

   const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      return false;
   };

   const onClearClicked = () => {
      setSearchText('');
      setHasFocus(false);
   };

   const userAuthoredComment = !!comments?.some(c => c.authorId === config.user.TechSmithId || c.replies.some(r => r.authorId === config.user.TechSmithId));

   return (
      <CommentFilterWrapper hasSearchText={!!searchText.length}>
         <CommentSearchBarContainer
            theme={theme}
            hasFocus={hasFocus}
         >
            <CommentSearchForm
               onSubmit={handleOnSubmit}
               hasFocus={hasFocus}
            >
               <CommentCountLabel
                  hasSearchText={!!searchText.length}
                  hasFocus={hasFocus}
               >
                  {!(comments?.length > 0) ? t('conversations.commentFilterEmptyLabel') : `${comments.length === 1 ? t('conversations.singleCommentFilterLabel') : t('conversations.multipleCommentsFilterLabel', {count: comments.length})}`}
               </CommentCountLabel>
               <CommentSearchWrapper
                  hasFocus={hasFocus}
                  theme={theme}
               >
                  <CommentSearchInput
                     disabled={!(comments?.length > 0)}
                     value={searchText}
                     onBlur={() => !searchText.length ? setHasFocus(false) : null}
                     theme={theme}
                     hasFocus={hasFocus}
                     maxLength={80}
                     aria-owns="search-results"
                     aria-label={t('search.search')}
                     onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value)}
                     ref={inputRef}
                     className="t-search-box"
                     onKeyPress={onKeyPress}
                     data-test-id="comment-filter-input"
                  />
                  <ClearButtonWrapper
                     isVisible={!!searchText.length}
                     inputHasFocus={hasFocus}
                  >
                     <ControlButton
                        themeName={theme}
                        glyph={<Glyphs.Close16x16 />}
                        buttonType={TscButtonType.tertiary}
                        title={t('search.clear')}
                        testId="clear-comment-filter-button"
                        onClick={onClearClicked}
                     />
                  </ClearButtonWrapper>
                  <SearchButtonWrapper>
                     <ControlButton
                        disabled={!(comments?.length > 0)}
                        themeName={theme}
                        glyph={<Glyphs.Search16x16 />}
                        buttonType={TscButtonType.tertiary}
                        title={t('search.search')}
                        testId="t-search-icon"
                        onClick={onSearchButtonClicked}
                     />
                  </SearchButtonWrapper>
               </CommentSearchWrapper>
               <NotificationToggleWrapper>
                  {config.user.IsSignedIn && (config.featureSwitches.IsScreencast || config.watchConfig.isOwner || userAuthoredComment) && (!config.featureSwitches.IsScreencast || VideoReviewId) && <NotificationToggle userAuthoredComment={userAuthoredComment} />}
               </NotificationToggleWrapper>
            </CommentSearchForm>
         </CommentSearchBarContainer>
      </CommentFilterWrapper>
   );
};

export interface ICommentFilterProps {
   searchText: string;
   setSearchText(text: string): void;
   executeSearch(): void;
}

export interface IStateMappedProps {
   comments: IComment[];
   VideoReviewId: string;
}

const mapStatesToProps = (
   {searchText, setSearchText, executeSearch}: ICommentFilterProps,
   {comments}: Partial<IStateMappedProps>,
   {VideoReviewId}: Partial<IStateMappedProps>
): ICommentFilterProps & IStateMappedProps => ({searchText, setSearchText, executeSearch, comments, VideoReviewId});

export default withMemoizedContexts(mapStatesToProps, commentsStore, selectedConversationStore)(CommentFilterBase);
